import React, { FC } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { styled, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { GridTooltip } from '../General/GridTooltip';
import { ISearchResult, IEntitlementInfo, IEntitlementId } from '../../types/search';

interface SearchResultsDataGridProps {
  searchResults: ISearchResult[] | null;
  loading: boolean;
  setResultsPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setResultsPageSize: React.Dispatch<React.SetStateAction<number>>;
  resultsPageNumber: number;
  resultsPageSize: number;
}

const SearchResultsDataGrid: FC<SearchResultsDataGridProps> = ({
  searchResults,
  setResultsPageNumber,
  setResultsPageSize,
  resultsPageNumber,
  resultsPageSize,
  loading,
}) => {
  const navigate = useNavigate();
  const selectUser = (user: ISearchResult) => {
    navigate(`../user/detail/${user.id}`);
  };

  // Helper function to aggregate entitlement field values
  const getEntitlementValues = (
    entitlements: IEntitlementInfo[],
    field: keyof IEntitlementId
  ): string => {
    if (!entitlements || entitlements.length === 0) return '';
    return entitlements.map((ent) => ent.id[field]).join(' | ');
  };

  // Custom tooltip component for multi-value cells
  const MultiValueTooltip: FC<{ params: GridRenderCellParams }> = ({ params }) => {
    const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
      <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
      [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#666666',
        maxWidth: 400,
        fontSize: theme.typography.pxToRem(14),
        inset: 'auto auto 0px 0px',
        padding: '8px 12px',
      },
    }));

    const values = params.value ? String(params.value).split(' | ') : [];

    return (
      <HtmlTooltip
        placement="bottom-start"
        title={
          <>
            <Typography sx={{ color: 'inherit', fontWeight: 'bold', marginBottom: '4px' }}>
              {params.colDef.headerName}
            </Typography>
            {values.map((value, index) => (
              <div key={index}>
                <em>{value}</em>
              </div>
            ))}
          </>
        }>
        <span className="table-cell-truncate">{params.value}</span>
      </HtmlTooltip>
    );
  };

  const pageOptions = [10, 25, 50];
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Identity ID',
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: 'firstName',
      headerName: 'First name',
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: 'subscriptionId',
      headerName: 'Subscription ID',
      width: 140,
      valueGetter: (value, row) => getEntitlementValues(row.entitlements, 'sourceId'),
      renderCell: (params: GridRenderCellParams) => <MultiValueTooltip params={params} />,
    },
    {
      field: 'system',
      headerName: 'System',
      width: 140,
      valueGetter: (value, row) => getEntitlementValues(row.entitlements, 'system'),
      renderCell: (params: GridRenderCellParams) => <MultiValueTooltip params={params} />,
    },
    {
      field: 'process',
      headerName: 'Process',
      width: 140,
      valueGetter: (value, row) => getEntitlementValues(row.entitlements, 'process'),
      renderCell: (params: GridRenderCellParams) => <MultiValueTooltip params={params} />,
    },
  ];

  return (
    searchResults && (
      <DataGrid
        autoHeight
        columns={columns}
        getRowId={() => Math.floor(Math.random() * 100000)}
        loading={loading}
        paginationMode="server"
        paginationModel={{
          page: searchResults.length > resultsPageSize ? 0 : resultsPageNumber,
          pageSize: !pageOptions.includes(searchResults.length)
            ? resultsPageSize
            : searchResults.length,
        }}
        onPaginationModelChange={({ page, pageSize }) => {
          setResultsPageNumber(page);
          setResultsPageSize(pageSize);
        }}
        onRowClick={(row) => selectUser(row.row)}
        rowCount={100}
        pageSizeOptions={pageOptions}
        rows={searchResults}
        sx={{
          backgroundColor: 'white',
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
      />
    )
  );
};

export default SearchResultsDataGrid;
